import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { every, isEmpty } from 'lodash';
import classnames from 'classnames';
import { useQueryClient } from 'react-query';
import { Col, Section } from '@fiverr-private/fit';
import { getContext } from '@fiverr-private/fiverr_context';
import { BusinessVettedFreelancerBanner } from '@fiverr-private/experts';
import { ComplianceSection } from '@fiverr-private/compliance';
import { DEFAULT_FIVERR_LOCALE } from '@fiverr-private/localization';
import { Stack } from '@fiverr-private/layout_components';
import { LoyaltyIndicationBannerPointsMode, LoyaltyIndicationBanner } from '@fiverr-private/loyalty_indication';
import { LoyaltyProgramTier, Gql } from '@fiverr-private/loyalty_utils';
import pathfinder from '@fiverr-private/pathfinder';
import {
    Expertise,
    UGCTranslateButton,
    SellerOverview,
    Testimonials,
    SafeWrapper,
} from '@fiverr-private/seller_pages_toolbox';
import { SOURCES } from '../../monitoring/monitoring-constants';
import { PAGE_NAME, useMonitoring } from '../../monitoring/index';
import { BASE_KEY as SELLER_DATA_QUERY_KEY } from '../../hooks/useSellerDataQuery';
import { BASE_KEY as NOTABLE_CLIENTS_DATA_QUERY_KEY } from '../../hooks/useNotableClientsQuery';
import { LazyNotableClients } from '../common/NotableClients';
import { useFreelancerPageContext } from '../FreelancerPageContext';
import { useRolloutContext } from '../../contexts/RolloutContext';
import { ButtonsGroup, DotsMenuButton, LearnMoreButton } from '../common/ActionButtons';
import { SaveToListButton } from '../common/ActionButtons/SaveToListButton';
import { ProOverview } from '../common/ProOverview';
import { SILVER_LOYALTY_INDICATION_POINTS } from '../../constants/loyaltyIndications';
import { CustomerProtectionBanner } from './CustomerProtectionBanner/CustomerProtectionBanner';
import { FreelancerReviews } from './FreelancerReviews';
import { AboutMe } from './AboutMe';
import { ContactCard } from './ContactCard';
import { GigServices } from './GigServices';
import { LazyPortfolio, usePortfolio } from './FreelancerPortfolio';
import { StickyHeader } from './StickyHeader';
import { StickyFooter } from './StickyFooter';
import { VacationMessageBanner } from './VacationMessageBanner';
import HelpBar from './HelpBar';
import Highlights from './Highlights';
import { SellerCounts } from './SellerCounts';
import { FreelancerWorkExperience } from './FreelancerWorkExperience';
import { UpmarketBanners } from './UpmarketBanners';

import styles from './index.module.scss';

export const FreelancerPage: React.FC = () => {
    const bannerRef = useRef<HTMLDivElement>(null);
    const [rerenderKey, setRerenderKey] = useState(0);
    // workaround to re-render the compliance section to remove auto applied styles
    useEffect(() => {
        setRerenderKey(Date.now());
        return () => {
            setRerenderKey(0);
        };
    }, []);

    const {
        isOnline,
        username,
        gigs,
        reviewsData,
        seller,
        hasReviews,
        hasTestimonials,
        isLoggedIn,
        isEligibleSeller,
        isCurrentUser,
        sellerId,
        notableClients,
        highlightsData,
        sellerTimeZone,
        pushkinConfiguration,
        isExperiential,
        loyaltyData,
    } = useFreelancerPageContext();
    const { hasPortfolio, showPortfolio } = usePortfolio();
    const {
        inQualasNewLevelSystemRollout,
        inStorksBackgroundCheckRollout,
        inZebrasNewLoyaltyPointsRollout,
        inMarlinsLoyaltySunsetRollout,
    } = useRolloutContext();

    const { fireEvents, Providers } = useMonitoring();
    const queryClient = useQueryClient();

    const { organizationId, experience, locale, userId } = getContext();
    const isDefaultLocale = locale === DEFAULT_FIVERR_LOCALE;

    const [showTranslatedUGC, setShowTranslatedUGC] = useState(false);

    const showProOverview = seller?.isPro;

    useEffect(() => {
        fireEvents({
            [Providers.Mixpanel]: {
                type: 'Viewed page',
                'Has portfolio': hasPortfolio,
            },
        });

        if (hasPortfolio) {
            fireEvents({
                [Providers.Mixpanel]: {
                    type: 'Seller portfolio exists',
                },
            });
        }
    }, [hasPortfolio, Providers.Mixpanel, fireEvents]);

    const observableRef = useRef(null);

    const showBusinessBanner = !isCurrentUser && !experience.isBusiness && isEligibleSeller && !organizationId;

    const showLoyaltyBanner = useMemo(() => {
        if (inMarlinsLoyaltySunsetRollout) return false;

        return (
            (loyaltyData?.tier === LoyaltyProgramTier.SILVER &&
                (loyaltyData?.orderEntitledForCredit as unknown as Gql.OrderEntitledForCredit) !==
                    Gql.OrderEntitledForCredit.AlreadyUsed) ||
            (loyaltyData?.tier === LoyaltyProgramTier.GOLD && seller?.isPro) ||
            loyaltyData?.tier === LoyaltyProgramTier.PLATINUM ||
            (loyaltyData?.tier === LoyaltyProgramTier.DIAMOND && inZebrasNewLoyaltyPointsRollout)
        );
    }, [inMarlinsLoyaltySunsetRollout, loyaltyData?.tier, seller?.isPro, inZebrasNewLoyaltyPointsRollout]);

    const onUGCTranslateSuccess = useCallback(
        (
            {
                oneLinerTitle = '',
                certifications = [],
                description = '',
                vacationMessage = '',
                notableClients: updatedNotableClients = [],
            } = {} as any,
            showTranslatedUGC: boolean
        ) => {
            const existingCertifications = seller?.certifications;
            const updatedCertifications = Array.isArray(existingCertifications)
                ? existingCertifications.map((cert, i) => ({
                      ...cert,
                      certificationName: certifications[i],
                  }))
                : [];

            queryClient.setQueryData([SELLER_DATA_QUERY_KEY, username], {
                ...seller,
                vacation: {
                    ...(seller?.vacation || {}),
                    awayMessage: vacationMessage,
                },
                oneLinerTitle,
                description,
                certifications: updatedCertifications,
            });

            const notableClientsArr = notableClients && notableClients.length > 0 ? notableClients : [];
            queryClient.setQueriesData(
                [NOTABLE_CLIENTS_DATA_QUERY_KEY, sellerId],
                notableClientsArr.map((client, i) => ({
                    ...client,
                    enriched_details: {
                        ...client.enriched_details,
                        description: updatedNotableClients[i].description,
                    },
                    interaction_description: updatedNotableClients[i].interactionDescription,
                }))
            );

            setShowTranslatedUGC(showTranslatedUGC);
        },
        [queryClient, username, notableClients, seller, sellerId]
    );

    const getUgc = useCallback(() => {
        const notableClientsArr = notableClients && notableClients.length > 0 ? notableClients : [];

        return {
            description: seller?.description,
            certifications: seller?.certifications?.map((cert) => cert.certificationName) || [],
            oneLinerTitle: seller?.oneLinerTitle,
            vacationMessage: seller?.vacation?.awayMessage,
            notableClients: notableClientsArr.map((client) => ({
                description: client?.enriched_details?.description || '',
                interactionDescription: client?.interaction_description,
            })),
        };
    }, [seller, notableClients]);

    if (!seller) {
        return null;
    }
    const sellerGigs = seller.gigs || [];
    const shouldShowComplianceSection =
        seller.isActive &&
        !!inStorksBackgroundCheckRollout &&
        userId !== Number(sellerId) &&
        !isEmpty(sellerGigs) &&
        every(sellerGigs, (gig) => gig.status !== 'ONHOLD');

    return (
        <div className={classnames(styles.freelancerPage, 'p-t-0 p-b-8')}>
            <StickyHeader
                seller={seller}
                observeHideRef={bannerRef}
                sellerTimeZone={sellerTimeZone}
                isOnline={isOnline}
            />
            <HelpBar />
            <div ref={bannerRef}></div>
            <Section className="p-t-0 p-b-0">
                <div className={classnames(styles.gridAboutSection, styles.withSecondColumn)}>
                    <div className={styles.firstColumn}>
                        <SellerOverview
                            className={classnames(styles.subHeaderOverview)}
                            canEdit={isCurrentUser && isEligibleSeller}
                            showSellerLevelNewBadges={inQualasNewLevelSystemRollout}
                        />
                        <div className={classnames(styles.firstColumnMaxWidth)} id={StickyHeader.TabName.About}>
                            {!isDefaultLocale && (
                                <div className="m-b-40">
                                    <UGCTranslateButton
                                        componentName={PAGE_NAME}
                                        sourceName="freelancer_page"
                                        translateURL={pathfinder('seller_page_ugc_translate')}
                                        getUgc={getUgc}
                                        onSuccess={onUGCTranslateSuccess}
                                    />
                                </div>
                            )}
                            <VacationMessageBanner />
                            {showProOverview && (
                                <ProOverview
                                    className={styles.firstColumnMaxWidth}
                                    proSubcategories={seller.proSubCategories}
                                />
                            )}
                            {seller.description && (
                                <AboutMe className="m-b-24" username={username} description={seller.description} />
                            )}
                            <Expertise
                                className="m-b-24"
                                filterType={showProOverview ? 'only_skills' : 'none'}
                                shouldLinkSkills={false}
                            />
                            <LazyNotableClients user={seller.user} source={SOURCES.page} />
                            <Highlights
                                sellerData={seller}
                                highlightsData={highlightsData}
                                className={classnames(styles.highlights, 'm-t-32')}
                            />
                            {shouldShowComplianceSection && (
                                <Stack marginBottom="6" marginTop="10">
                                    <ComplianceSection
                                        key={rerenderKey}
                                        requeseteeId={Number(sellerId)}
                                        requeseteeUserName={seller.user.name}
                                    />
                                </Stack>
                            )}
                            <ButtonsGroup
                                className={classnames(styles.hideMd, styles.buttonsGroup, 'flex flex-start m-t-40')}
                            >
                                <LearnMoreButton />
                                <SaveToListButton sellerId={sellerId} />
                            </ButtonsGroup>
                        </div>
                    </div>
                    <div
                        className={classnames(
                            styles.secondColumn,
                            styles.noCoverPhoto,
                            'm-t-24 p-t-8 flex flex-col flex-items-end'
                        )}
                    >
                        <ButtonsGroup className={styles.buttonsGroup}>
                            <LearnMoreButton className="text-trunc" />
                            <SaveToListButton sellerId={sellerId} />
                            <DotsMenuButton user={seller.user} source={SOURCES.page} isLoggedIn={isLoggedIn} />
                        </ButtonsGroup>
                        {/* TODO: Set numbers of counts */}
                        <SellerCounts className="m-b-40 p-b-8" ordersCount={undefined} repeatBuyersCount={undefined} />
                        <ContactCard
                            username={username}
                            timeZone={sellerTimeZone}
                            isOnline={isOnline}
                            user={seller?.user}
                            newView
                            respondHours={seller.responseTime?.inHours}
                        />
                        {showLoyaltyBanner && (
                            <Stack hidden={{ default: true, md: false }} marginY="6">
                                {inZebrasNewLoyaltyPointsRollout ? (
                                    <LoyaltyIndicationBannerPointsMode
                                        type="freelancer_page"
                                        loyaltyIndicationData={loyaltyData as unknown as Gql.EngagementBadgeInfo}
                                        pointsAmount={
                                            loyaltyData?.tier === LoyaltyProgramTier.SILVER
                                                ? SILVER_LOYALTY_INDICATION_POINTS
                                                : 0
                                        }
                                        gigInfo={{
                                            gigPriceInUSDCents: 360000,
                                            isProSeller: seller?.isPro,
                                            sellerName: seller?.user?.profile?.displayName || username,
                                        }}
                                    />
                                ) : (
                                    <LoyaltyIndicationBanner
                                        type="freelancer_page"
                                        loyaltyIndicationData={loyaltyData as unknown as Gql.EngagementBadgeInfo}
                                        gigInfo={{
                                            gigPriceInUSDCents: 360000,
                                            isProSeller: seller?.isPro,
                                            sellerName: seller?.user?.profile?.displayName || username,
                                        }}
                                    />
                                )}
                            </Stack>
                        )}
                        {isEligibleSeller && !showLoyaltyBanner && <CustomerProtectionBanner marginY="6" />}
                    </div>

                    {showBusinessBanner && (
                        <Col lg={8} md={12}>
                            <BusinessVettedFreelancerBanner className="m-t-24" source={PAGE_NAME} />
                        </Col>
                    )}
                </div>
                <div id={StickyHeader.TabName.Services}>
                    <GigServices gigs={gigs} showTranslatedUGC={showTranslatedUGC} />
                </div>
                <div className={classnames('flex flex-col', isExperiential && 'flex-col-reverse')}>
                    {seller?.isPro && <FreelancerWorkExperience pushkinConfiguration={pushkinConfiguration} />}
                    {showPortfolio && (
                        <div className={styles.portfolio} id={StickyHeader.TabName.Portfolio}>
                            <LazyPortfolio />
                        </div>
                    )}
                </div>
            </Section>
            <UpmarketBanners />
            {hasReviews && reviewsData && (
                <Section className="p-t-0 p-b-0">
                    <div ref={observableRef} className={styles.reviews} id={StickyHeader.TabName.Reviews}>
                        <FreelancerReviews reviewsData={reviewsData} />
                    </div>
                </Section>
            )}
            {hasTestimonials && seller?.testimonials && (
                <Section className="p-t-0 p-b-0">
                    <div className={styles.recommendations} id={StickyHeader.TabName.Testimonials}>
                        <Testimonials />
                    </div>
                </Section>
            )}
            <StickyFooter
                username={username}
                respondHours={seller.responseTime?.inHours}
                hourlyRateInUsdCents={seller?.hourlyRate?.priceInCents}
            />
        </div>
    );
};

export default SafeWrapper(FreelancerPage, 'FreelancerPage');
